import { H1, P, Section, Skeleton, Space } from '@dnb/eufemia';
import type { SpaceType } from '@dnb/eufemia/shared/types';
import classNames from 'classnames';
import { type JSX, type PropsWithChildren, type ReactNode, useEffect } from 'react';
import { useLocation } from 'react-router-dom';

import useReturnTo from '@/hooks/useReturnTo';

import Container from '../Container';
import Footer from '../Footer';
import NavigationBar from '../NavigationBar';

import style from './index.module.css';

type PageProps = PropsWithChildren<{
  title: ReactNode;
  tabTitle?: string;
  subtitle?: ReactNode;
  illustration?: ReactNode;
  description?: ReactNode;
  component?: ReactNode;
  skeleton?: boolean;
  noContainer?: boolean;
  styleType?: string;
  innerSpace?: SpaceType;
}>;

export default function Page({
  title,
  subtitle,
  illustration,
  description,
  children,
  component,
  tabTitle,
  skeleton = false,
  noContainer = false,
  styleType = 'white',
  innerSpace = 'xx-large',
}: PageProps): JSX.Element {
  const location = useLocation();
  const setReturnTo = useReturnTo()[1];

  useEffect(() => {
    const newTitle = tabTitle ?? 'DNB Developer';
    if (newTitle) document.title = newTitle;
  }, [tabTitle, title]);

  useEffect(() => {
    setReturnTo(location.pathname);
  }, [location.pathname, setReturnTo]);

  return (
    <div
      className={classNames({
        [style['Page']!]: true,
        [style['Page--illustrated']!]: !!illustration,
      })}
    >
      <NavigationBar />
      <Skeleton className={style['SkeletonWrapper']} show={skeleton}>
        <Section backgroundColor="var(--color-mint-green-25)" innerSpace="large" style={{ zIndex: 2 }}>
          <Container centered className={style['HeaderContent']}>
            <H1 className={style['HeaderTitle']}>
              {subtitle && <span className={style['Subtitle']}>{subtitle}</span>}
              {title}
            </H1>
            <div className={style['HeaderDescription']}>
              {description &&
                (typeof description === 'string' ? (
                  <P top="large">{description}</P>
                ) : (
                  <Space top="large">{description}</Space>
                ))}
              {component}
            </div>
            <div className={style['HeaderIllustration']}>{illustration}</div>
          </Container>
        </Section>
        <Section backgroundColor={styleType} className={style['Content']} innerSpace={innerSpace}>
          {noContainer ? children : <Container centered>{children}</Container>}
        </Section>
      </Skeleton>
      <Footer />
    </div>
  );
}
